import { ChevronRight } from 'react-feather'
import { Card, CardBody, CardText, CardTitle } from 'reactstrap'
import { COLOR_MAP, ICON_MAP } from '../../config/templateConfig'
import { useNavigate } from 'react-router-dom'

// Import ReactFeather icons dynamically
const iconComponents = {
  Info: require('react-feather').Info,
  FileText: require('react-feather').FileText,
  HelpCircle: require('react-feather').HelpCircle,
  Settings: require('react-feather').Settings,
  BarChart: require('react-feather').BarChart,
  TrendingUp: require('react-feather').TrendingUp,
  PieChart: require('react-feather').PieChart,
  Grid: require('react-feather').Grid,
  Home: require('react-feather').Home,
  User: require('react-feather').User,
  Bell: require('react-feather').Bell,
  Calendar: require('react-feather').Calendar,
  Star: require('react-feather').Star,
  Shield: require('react-feather').Shield,
  Globe: require('react-feather').Globe,
  Download: require('react-feather').Download,
}

const GenericLinkWidget = ({
  title,
  description,
  icon,
  color,
  url,
  half = false,
  mode = 'secondary'
}) => {
  const navigate = useNavigate()
  // Get icon component
  const IconComponent = iconComponents[ICON_MAP[icon]] || iconComponents.Info

  // Get color from map
  const cardColor = COLOR_MAP[color] || COLOR_MAP.blue

  const handleClick = () => {
    if (url && url !== '#') {
      try {
        // Remove spaces inside the URL that might cause URL constructor to fail
        const cleanUrl = url.replace(/\s+/g, '')

        if (cleanUrl.startsWith('http')) {
          const urlObj = new URL(cleanUrl)
          if (
            urlObj.hostname === window.location.hostname &&
            urlObj.port === window.location.port
          ) {
            navigate(urlObj.pathname + urlObj.search + urlObj.hash)
          } else {
            window.location.href = cleanUrl
          }
        } else if (cleanUrl.startsWith('www')) {
          window.location.href = `https://${cleanUrl}`
        } else {
          navigate(cleanUrl)
        }
      } catch (error) {
        // Fallback to internal navigation if URL parsing fails
        console.error('Invalid URL format:', url)
        navigate(url.replace(/\s+/g, ''))
      }
    }
  }

  // Handle Mode Styling
  let paddingClass = 'p-4'
  let titleClass = 'fw-bold mb-1'
  let iconSize = 24
  let iconPaddingClass = 'p-2 me-3'
  let descClass = 'mb-0'
  let showUrlInfo = true

  let isTinySquare = mode === 'tiny-square'
  let isTiny = mode === 'tiny' || isTinySquare

  if (mode === 'primary') {
    paddingClass = 'p-4'
    titleClass = 'fw-bold mb-1'
    iconSize = 36
    iconPaddingClass = 'p-3 me-3'
    descClass = 'mb-0'
  } else if (mode === 'compact') {
    paddingClass = 'p-3'
    titleClass = 'fw-bold mb-1'
    iconSize = 18
    iconPaddingClass = 'p-2 me-2'
    descClass = 'mb-0'
    showUrlInfo = false
  } else if (isTiny) {
    paddingClass = isTinySquare ? 'p-2' : 'p-2 px-3'
    titleClass = `fw-bold mb-0 text-truncate ${isTinySquare ? 'text-center' : 'text-start'}`
    iconSize = 16
    iconPaddingClass = 'p-2'
    showUrlInfo = false
  }

  return (
    <div className={`${!isTiny ? 'h-100' : ''} ${half ? 'col-lg-6' : ''}`}>
      <Card
        className={`${!isTiny ? 'h-100' : ''} border-0 shadow-sm transition-all`}
        data-testid={`homepage-generic-link-${title}`}
        style={{
          background: cardColor,
          backgroundImage: 'linear-gradient(135deg, rgba(255,255,255,0.15) 0%, rgba(0,0,0,0.05) 100%)',
          color: '#ffffff',
          border: 'none',
          cursor: 'pointer',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          aspectRatio: isTinySquare ? '1/1' : undefined,
          width: '100%',
          minHeight: isTinySquare ? undefined : mode === 'tiny' ? '68px' : mode === 'primary' ? '236px' : mode === 'compact' ? '110px' : '152px',
          borderRadius: isTiny ? '12px' : '20px',
          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
          height: isTiny ? (isTinySquare ? 'auto' : '100%') : '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: isTinySquare ? 'center' : 'flex-start'
        }}
        onClick={handleClick}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-5px)'
          e.currentTarget.style.boxShadow = '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)'
          e.currentTarget.style.boxShadow = '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
        }}
      >
        <CardBody className={`${paddingClass} ${isTiny ? '' : 'flex-grow-1'} d-flex flex-column justify-content-center ${isTinySquare ? 'align-items-center justify-content-center text-center' : ''}`}>
          <div className={isTinySquare ? 'd-flex flex-column align-items-center justify-content-center mb-0 w-100' : `d-flex align-items-center w-100 ${mode === 'primary' ? 'mb-2' : 'mb-0'}`}>
            <div
              className={`${iconPaddingClass} ${isTinySquare ? 'mb-2' : 'me-3'} rounded-circle d-flex align-items-center justify-content-center`}
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.15)',
                backdropFilter: 'blur(8px)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                color: '#ffffff',
                flexShrink: 0,
                width: mode === 'primary' ? '64px' : (mode === 'compact' || isTiny) ? '36px' : '48px',
                height: mode === 'primary' ? '64px' : (mode === 'compact' || isTiny) ? '36px' : '48px',
                boxShadow: 'inset 0 0 10px rgba(255,255,255,0.1)'
              }}
            >
              <IconComponent size={iconSize} />
            </div>
            <div className={isTinySquare ? 'w-100' : 'flex-grow-1 d-flex flex-column justify-content-center text-start'}>
              <CardTitle
                tag={mode === 'primary' ? 'h5' : isTiny ? 'span' : 'h6'}
                className={`${titleClass} ${isTiny ? 'd-block text-truncate' : ''} text-white`}
                style={{ 
                  fontSize: isTiny ? '14px' : mode === 'primary' ? '28px' : mode === 'compact' ? '15px' : '18px',
                  fontWeight: 600,
                  marginBottom: !isTiny ? '2px' : '0',
                  lineHeight: 1.1
                }}
              >
                {title}
              </CardTitle>
              {!isTiny && (
                <div className="small d-flex align-items-center" style={{ color: 'rgba(255,255,255,0.7)', fontSize: mode === 'compact' ? '11px' : '12px', marginTop: mode === 'primary' ? '4px' : '2px' }}>
                  {showUrlInfo && <ChevronRight size={10} className="me-1" />}
                  <span>Lihat {title}</span>
                </div>
              )}
            </div>
          </div>

          {!isTiny && description && (
            <CardText className={`mt-2 ${descClass}`} style={{ color: 'rgba(255,255,255,0.7)', fontSize: mode === 'compact' ? '12px' : '14px', lineHeight: '1.4' }}>{description}</CardText>
          )}
        </CardBody>
      </Card>
    </div>
  )
}

export default GenericLinkWidget
